import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProductoCombobox from "@/components/ProductoCombobox";
import {
  Plus, PencilSimple, Trash, Play, ForkKnife, Package,
  ClockCounterClockwise, Warning, CheckCircle, MagnifyingGlass, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import NumberInput from "@/components/ui/NumberInput";

const fmtMoney = (n) => `$ ${Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n, d = 3) => Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: d });
const fmtDate = (iso) => {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const EMPTY_RECETA = {
  nombre: "",
  descripcion: "",
  tipo: "transformacion",
  producto_resultante_id: "",
  producto_resultante_nombre: "",
  rendimiento_kg: 1,
  unidad_rendimiento: "kg",
  ingredientes: [],
  activa: true,
};

export default function RecetasPage() {
  const [recetas, setRecetas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // objeto receta (con id si es edit)
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [executing, setExecuting] = useState(null); // receta a ejecutar
  const [execCantidad, setExecCantidad] = useState("");
  const [execPreview, setExecPreview] = useState(null);
  const [execObservaciones, setExecObservaciones] = useState("");
  const [execRunning, setExecRunning] = useState(false);
  const [historyReceta, setHistoryReceta] = useState(null);
  const [ejecuciones, setEjecuciones] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/recetas");
      setRecetas(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recetas;
    return recetas.filter((r) =>
      (r.nombre || "").toLowerCase().includes(q)
      || (r.producto_resultante_nombre || "").toLowerCase().includes(q)
      || (r.descripcion || "").toLowerCase().includes(q)
    );
  }, [recetas, search]);

  const openNew = () => setEditing({ ...EMPTY_RECETA });
  const openEdit = (r) => setEditing({ ...r });

  const setEditField = (k, v) => setEditing((cur) => ({ ...cur, [k]: v }));

  const addIngrediente = () => setEditing((cur) => ({
    ...cur,
    ingredientes: [...(cur.ingredientes || []), {
      tipo: "producto", ref_id: "", ref_nombre: "", cantidad: 1, unidad: "kg",
    }],
  }));
  const removeIngrediente = (idx) => setEditing((cur) => ({
    ...cur,
    ingredientes: (cur.ingredientes || []).filter((_, i) => i !== idx),
  }));
  const setIngredienteField = (idx, k, v) => setEditing((cur) => ({
    ...cur,
    ingredientes: (cur.ingredientes || []).map((it, i) => i === idx ? { ...it, [k]: v } : it),
  }));

  const saveReceta = async () => {
    if (!editing?.nombre?.trim()) { toast.error("El nombre es obligatorio"); return; }
    if (!editing?.producto_resultante_id) { toast.error("Elegí el producto resultante"); return; }
    if (!editing?.rendimiento_kg || Number(editing.rendimiento_kg) <= 0) {
      toast.error("El rendimiento debe ser mayor a 0"); return;
    }
    if (!editing.ingredientes || editing.ingredientes.length === 0) {
      toast.error("Agregá al menos 1 ingrediente"); return;
    }
    for (const [i, ing] of editing.ingredientes.entries()) {
      if (!ing.ref_id) { toast.error(`Ingrediente #${i + 1}: seleccioná el producto/insumo`); return; }
      if (!ing.cantidad || Number(ing.cantidad) <= 0) {
        toast.error(`Ingrediente #${i + 1}: la cantidad debe ser > 0`); return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        nombre: editing.nombre.trim(),
        descripcion: editing.descripcion || null,
        tipo: editing.tipo || null,
        producto_resultante_id: editing.producto_resultante_id,
        producto_resultante_nombre: editing.producto_resultante_nombre,
        rendimiento_kg: Number(editing.rendimiento_kg),
        unidad_rendimiento: editing.unidad_rendimiento || "kg",
        ingredientes: editing.ingredientes.map((it) => ({
          tipo: it.tipo,
          ref_id: it.ref_id,
          ref_nombre: it.ref_nombre,
          cantidad: Number(it.cantidad),
          unidad: it.unidad || "kg",
        })),
        activa: editing.activa !== false,
      };
      if (editing.id) {
        await api.put(`/recetas/${editing.id}`, payload);
        toast.success("Receta actualizada");
      } else {
        await api.post("/recetas", payload);
        toast.success("Receta creada");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/recetas/${confirmDelete.id}`);
      toast.success("Receta eliminada");
      setConfirmDelete(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  // ----- Ejecutar -----
  const openExecute = (r) => {
    setExecuting(r);
    setExecCantidad(String(r.rendimiento_kg || 1));
    setExecPreview(null);
    setExecObservaciones("");
  };

  const loadPreview = async () => {
    if (!executing || !execCantidad || Number(execCantidad) <= 0) {
      setExecPreview(null); return;
    }
    try {
      const { data } = await api.get(`/recetas/${executing.id}/preview`, {
        params: { cantidad: Number(execCantidad) },
      });
      setExecPreview(data.plan);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al calcular");
    }
  };
  useEffect(() => {
    if (executing && execCantidad) {
      const t = setTimeout(loadPreview, 250);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line
  }, [executing?.id, execCantidad]);

  const runReceta = async () => {
    if (!executing) return;
    if (!execPreview?.suficiente_stock) {
      toast.error("Stock insuficiente para ejecutar"); return;
    }
    setExecRunning(true);
    try {
      const { data } = await api.post(`/recetas/${executing.id}/ejecutar`, {
        cantidad_a_producir: Number(execCantidad),
        observaciones: execObservaciones || null,
      });
      toast.success(`Ejecución OK — Lote ${data.lote_generado.numero} generado (${fmtNum(data.lote_generado.kilos_entrada)} kg · ${fmtMoney(data.lote_generado.costo_total)})`);
      setExecuting(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al ejecutar");
    } finally { setExecRunning(false); }
  };

  // ----- Historial -----
  const openHistory = async (r) => {
    setHistoryReceta(r);
    try {
      const { data } = await api.get(`/recetas/${r.id}/ejecuciones`);
      setEjecuciones(data || []);
    } catch { setEjecuciones([]); }
  };

  return (
    <>
      <Topbar
        title="Recetas de Producción"
        subtitle={`${recetas.length} receta${recetas.length !== 1 ? "s" : ""} · fórmulas reutilizables que consumen ingredientes y generan lotes`}
        right={
          <Button onClick={openNew} className="rounded-sm bg-[#6B1F24] hover:bg-[#571519] h-9" data-testid="receta-nueva-btn">
            <Plus size={14} className="mr-1" /> Nueva receta
          </Button>
        }
      />

      <div className="p-4 space-y-4">
        {/* Buscador */}
        <div className="bg-white border border-[#E5E5E5] rounded-sm p-3">
          <div className="relative max-w-md">
            <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Buscar por nombre, producto resultante o descripción…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-sm h-9 pl-8 text-[13px]"
              data-testid="receta-search"
            />
          </div>
        </div>

        {/* Tabla */}
        <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-neutral-50 text-[11px] uppercase font-mono text-[#666]">
                <tr>
                  <th className="px-4 py-2 text-left">Nombre</th>
                  <th className="px-4 py-2 text-left">Producto resultante</th>
                  <th className="px-4 py-2 text-right w-32">Rendimiento</th>
                  <th className="px-4 py-2 text-center w-24">Ingredientes</th>
                  <th className="px-4 py-2 text-center w-20">Estado</th>
                  <th className="px-4 py-2 text-right w-48">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="text-center py-6 text-[#999]">Cargando…</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-10 text-[#999]">
                    <ForkKnife size={26} weight="duotone" className="mx-auto text-neutral-300 mb-2" />
                    Todavía no hay recetas. Creá una para armar fórmulas reutilizables.
                  </td></tr>
                )}
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-neutral-100 hover:bg-neutral-50 group" data-testid={`receta-row-${r.id}`}>
                    <td className="px-4 py-2 align-top">
                      <div className="font-medium flex items-center gap-2">
                        {r.nombre}
                        {r.tipo === "combo" && (
                          <span className="text-[9px] font-mono font-bold uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm" data-testid={`receta-tipo-badge-${r.id}`}>
                            COMBO
                          </span>
                        )}
                        {r.tipo === "transformacion" && (
                          <span className="text-[9px] font-mono font-bold uppercase bg-neutral-100 text-neutral-600 px-1.5 py-0.5 rounded-sm" data-testid={`receta-tipo-badge-${r.id}`}>
                            TRANSF.
                          </span>
                        )}
                        {!r.tipo && (
                          <span className="text-[9px] font-mono uppercase bg-rose-50 text-rose-600 px-1.5 py-0.5 rounded-sm" title="Sin categoría — editá la receta para asignarla">
                            SIN CAT.
                          </span>
                        )}
                      </div>
                      {r.descripcion && <div className="text-[11px] text-neutral-500 mt-0.5">{r.descripcion}</div>}
                    </td>
                    <td className="px-4 py-2 align-top">
                      <div className="flex items-center gap-1.5">
                        <Package size={14} className="text-[#6B1F24]" />
                        <span>{r.producto_resultante_nombre}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right font-mono align-top">
                      {fmtNum(r.rendimiento_kg)} {r.unidad_rendimiento}
                    </td>
                    <td className="px-4 py-2 text-center align-top">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-neutral-100 text-neutral-700 text-[11px] font-mono">
                        {r.ingredientes?.length || 0}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center align-top">
                      {r.activa !== false ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                          <CheckCircle size={12} weight="fill" /> Activa
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] text-neutral-500">
                          <X size={12} weight="bold" /> Inactiva
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right align-top">
                      <div className="flex justify-end gap-1 opacity-70 group-hover:opacity-100">
                        <Button size="sm" variant="outline" className="rounded-sm h-7 px-2 text-[11px]"
                          onClick={() => openExecute(r)} data-testid={`receta-run-${r.id}`}>
                          <Play size={12} className="mr-1" /> Ejecutar
                        </Button>
                        <button onClick={() => openHistory(r)}
                          className="p-1.5 hover:bg-neutral-200 rounded-sm text-[#666] hover:text-[#1A1A1A]"
                          title="Ver historial" data-testid={`receta-hist-${r.id}`}>
                          <ClockCounterClockwise size={14} />
                        </button>
                        <button onClick={() => openEdit(r)}
                          className="p-1.5 hover:bg-neutral-200 rounded-sm text-[#666] hover:text-[#1A1A1A]"
                          title="Editar" data-testid={`receta-edit-${r.id}`}>
                          <PencilSimple size={14} />
                        </button>
                        <button onClick={() => setConfirmDelete(r)}
                          className="p-1.5 hover:bg-rose-50 rounded-sm text-[#666] hover:text-rose-700"
                          title="Eliminar" data-testid={`receta-del-${r.id}`}>
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Sheet: Editor de receta */}
      <Sheet open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">
              {editing?.id ? "Editar receta" : "Nueva receta"}
            </SheetTitle>
            <SheetDescription>
              Definí el producto resultante, el rendimiento por lote y los ingredientes.
            </SheetDescription>
          </SheetHeader>
          {editing && (
            <div className="mt-4 space-y-4 text-[13px]">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-[12px]">Nombre <span className="text-rose-600">*</span></Label>
                  <Input value={editing.nombre} onChange={(e) => setEditField("nombre", e.target.value)}
                    className="rounded-sm mt-1" data-testid="receta-nombre" placeholder="ej: Milanesa envasada 500g" />
                </div>
                <div className="col-span-2">
                  <Label className="text-[12px]">Descripción (opcional)</Label>
                  <Textarea value={editing.descripcion || ""} onChange={(e) => setEditField("descripcion", e.target.value)}
                    rows={2} className="rounded-sm mt-1" data-testid="receta-desc" placeholder="Notas o proceso" />
                </div>
                <div className="col-span-2">
                  <Label className="text-[12px]">Tipo de receta <span className="text-rose-600">*</span></Label>
                  <Select value={editing.tipo || "transformacion"} onValueChange={(v) => setEditField("tipo", v)}>
                    <SelectTrigger className="rounded-sm mt-1 h-10" data-testid="receta-tipo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transformacion">Con transformación (proceso físico: cortar, moler, envasar)</SelectItem>
                      <SelectItem value="combo">Combo (kit de venta armado con productos existentes)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-[12px]">Producto resultante <span className="text-rose-600">*</span></Label>
                  <div className="mt-1">
                    <ProductoCombobox
                      kind="productos"
                      value={editing.producto_resultante_id ? { codigo: "", nombre: editing.producto_resultante_nombre } : null}
                      onSelect={(p) => {
                        setEditField("producto_resultante_id", p.id);
                        setEditField("producto_resultante_nombre", p.nombre);
                      }}
                      placeholder="Elegí el producto que se produce…"
                      testid="receta-producto-resultante"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[12px]">Rendimiento por lote <span className="text-rose-600">*</span></Label>
                  <NumberInput step="0.01" min="0" value={editing.rendimiento_kg}
                    onChange={(e) => setEditField("rendimiento_kg", e.target.value)}
                    className="rounded-sm mt-1 font-mono" data-testid="receta-rendimiento" />
                </div>
                <div>
                  <Label className="text-[12px]">Unidad</Label>
                  <Select value={editing.unidad_rendimiento || "kg"}
                    onValueChange={(v) => setEditField("unidad_rendimiento", v)}>
                    <SelectTrigger className="rounded-sm mt-1 h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="unidad">unidad</SelectItem>
                      <SelectItem value="litro">litro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <input type="checkbox" checked={editing.activa !== false}
                    onChange={(e) => setEditField("activa", e.target.checked)}
                    id="receta-activa" data-testid="receta-activa" />
                  <Label htmlFor="receta-activa" className="text-[12px] cursor-pointer">
                    Receta activa (se puede ejecutar)
                  </Label>
                </div>
              </div>

              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-heading font-semibold text-[14px]">Ingredientes</div>
                    <div className="text-[11px] text-neutral-500">Cantidades por 1 lote de {editing.rendimiento_kg} {editing.unidad_rendimiento}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={addIngrediente} className="rounded-sm h-8" data-testid="receta-ing-add">
                    <Plus size={12} className="mr-1" /> Agregar
                  </Button>
                </div>
                {(editing.ingredientes || []).length === 0 && (
                  <div className="text-center py-4 text-[12px] text-neutral-400 border rounded-sm border-dashed">
                    Sin ingredientes. Agregá el primero.
                  </div>
                )}
                <div className="space-y-2">
                  {(editing.ingredientes || []).map((ing, idx) => (
                    <div key={idx} className="border rounded-sm p-2 bg-neutral-50/50 grid grid-cols-12 gap-2 items-end" data-testid={`receta-ing-row-${idx}`}>
                      <div className="col-span-3">
                        <Label className="text-[11px]">Tipo</Label>
                        <Select value={ing.tipo}
                          onValueChange={(v) => {
                            setIngredienteField(idx, "tipo", v);
                            setIngredienteField(idx, "ref_id", "");
                            setIngredienteField(idx, "ref_nombre", "");
                          }}>
                          <SelectTrigger className="rounded-sm mt-0.5 h-9 text-[12px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="producto">Producto</SelectItem>
                            <SelectItem value="insumo">Insumo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-5">
                        <Label className="text-[11px]">{ing.tipo === "insumo" ? "Insumo" : "Producto"}</Label>
                        <div className="mt-0.5">
                          <ProductoCombobox
                            kind={ing.tipo === "insumo" ? "insumos" : "productos"}
                            value={ing.ref_id ? { codigo: "", nombre: ing.ref_nombre } : null}
                            onSelect={(p) => {
                              setIngredienteField(idx, "ref_id", p.id);
                              setIngredienteField(idx, "ref_nombre", p.nombre);
                              if (!ing.unidad || ing.unidad === "kg") {
                                setIngredienteField(idx, "unidad", p.unidad || "kg");
                              }
                            }}
                            placeholder="Elegir…"
                            testid={`receta-ing-item-${idx}`}
                          />
                        </div>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[11px]">Cantidad</Label>
                        <NumberInput step="0.001" min="0" value={ing.cantidad}
                          onChange={(e) => setIngredienteField(idx, "cantidad", e.target.value)}
                          className="rounded-sm mt-0.5 h-9 font-mono text-[12px]" data-testid={`receta-ing-cant-${idx}`} />
                      </div>
                      <div className="col-span-1">
                        <Label className="text-[11px]">Unid.</Label>
                        <Input value={ing.unidad || "kg"}
                          onChange={(e) => setIngredienteField(idx, "unidad", e.target.value)}
                          className="rounded-sm mt-0.5 h-9 font-mono text-[11px]" />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button onClick={() => removeIngrediente(idx)}
                          className="p-1.5 hover:bg-rose-50 rounded-sm text-[#666] hover:text-rose-700"
                          title="Quitar" data-testid={`receta-ing-del-${idx}`}>
                          <Trash size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <SheetFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={saveReceta} disabled={saving}
              className="bg-[#6B1F24] hover:bg-[#571519]" data-testid="receta-guardar">
              {saving ? "Guardando…" : (editing?.id ? "Guardar cambios" : "Crear receta")}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Dialog: Ejecutar receta */}
      <Dialog open={!!executing} onOpenChange={(v) => !v && setExecuting(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Play size={18} weight="duotone" className="text-[#6B1F24]" />
              Ejecutar receta: {executing?.nombre}
            </DialogTitle>
            <DialogDescription>
              El stock de los ingredientes se descontará por FIFO y se generará un lote nuevo del producto resultante.
            </DialogDescription>
          </DialogHeader>
          {executing && (
            <div className="space-y-3 text-[13px]">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-[12px]">Cantidad a producir</Label>
                  <NumberInput step="0.01" min="0" value={execCantidad}
                    onChange={(e) => setExecCantidad(e.target.value)}
                    className="rounded-sm mt-1 font-mono" data-testid="exec-cantidad" />
                </div>
                <div>
                  <Label className="text-[12px]">Unidad</Label>
                  <div className="mt-1 h-10 flex items-center px-2 border rounded-sm bg-neutral-50 font-mono text-[12px]">
                    {executing.unidad_rendimiento || "kg"}
                  </div>
                </div>
                <div>
                  <Label className="text-[12px]">Factor (respecto a la receta)</Label>
                  <div className="mt-1 h-10 flex items-center px-2 border rounded-sm bg-neutral-50 font-mono text-[12px]">
                    ×{execPreview?.factor ?? (Number(execCantidad || 0) / Number(executing.rendimiento_kg || 1)).toFixed(4)}
                  </div>
                </div>
              </div>

              {execPreview && (
                <div className="border rounded-sm">
                  <div className="px-3 py-2 bg-neutral-50 border-b flex items-center justify-between">
                    <div className="text-[12px] font-medium">Plan de consumo</div>
                    {execPreview.suficiente_stock ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                        <CheckCircle size={12} weight="fill" /> Stock suficiente
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-rose-700">
                        <Warning size={12} weight="fill" /> Falta stock
                      </span>
                    )}
                  </div>
                  <table className="w-full text-[12px]">
                    <thead className="bg-neutral-50 text-[10px] uppercase font-mono text-[#666]">
                      <tr>
                        <th className="px-3 py-1.5 text-left w-16">Tipo</th>
                        <th className="px-3 py-1.5 text-left">Ingrediente</th>
                        <th className="px-3 py-1.5 text-right w-24">Requerido</th>
                        <th className="px-3 py-1.5 text-right w-24">Disponible</th>
                        <th className="px-3 py-1.5 text-right w-28">Costo estim.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {execPreview.ingredientes.map((ing, i) => (
                        <tr key={i} className={`border-t ${!ing.suficiente ? "bg-rose-50/40" : ""}`}>
                          <td className="px-3 py-1.5 text-[10px] uppercase font-mono text-neutral-600">{ing.tipo}</td>
                          <td className="px-3 py-1.5">
                            {ing.ref_nombre}
                            {ing.lotes && ing.lotes.length > 0 && (
                              <div className="text-[10px] text-neutral-500 font-mono mt-0.5">
                                {ing.lotes.map((l) => `${l.lote_numero || "s/n"}: ${fmtNum(l.kilos_a_consumir)}${ing.unidad}`).join(" · ")}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono">{fmtNum(ing.requerido)} {ing.unidad}</td>
                          <td className={`px-3 py-1.5 text-right font-mono ${!ing.suficiente ? "text-rose-700 font-bold" : "text-neutral-600"}`}>
                            {fmtNum(ing.stock_disponible)} {ing.unidad}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono">{fmtMoney(ing.costo_estimado)}</td>
                        </tr>
                      ))}
                      <tr className="border-t bg-neutral-50 font-medium">
                        <td colSpan={4} className="px-3 py-1.5 text-right">Costo total estimado</td>
                        <td className="px-3 py-1.5 text-right font-mono" data-testid="exec-costo-total">{fmtMoney(execPreview.costo_total)}</td>
                      </tr>
                      <tr className="border-t text-[11px] text-neutral-600">
                        <td colSpan={4} className="px-3 py-1.5 text-right">Costo por {executing.unidad_rendimiento || "kg"} resultante</td>
                        <td className="px-3 py-1.5 text-right font-mono">{fmtMoney(execPreview.costo_por_kg_resultante)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <div>
                <Label className="text-[12px]">Observaciones</Label>
                <Textarea value={execObservaciones} onChange={(e) => setExecObservaciones(e.target.value)}
                  rows={2} className="rounded-sm mt-1" placeholder="Opcional" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setExecuting(null)}>Cancelar</Button>
            <Button onClick={runReceta} disabled={execRunning || !execPreview?.suficiente_stock}
              className="bg-[#6B1F24] hover:bg-[#571519]" data-testid="exec-confirmar">
              <Play size={13} className="mr-1" /> {execRunning ? "Ejecutando…" : "Confirmar y ejecutar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Historial */}
      <Dialog open={!!historyReceta} onOpenChange={(v) => !v && setHistoryReceta(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ClockCounterClockwise size={18} weight="duotone" className="text-[#6B1F24]" />
              Historial de ejecuciones: {historyReceta?.nombre}
            </DialogTitle>
            <DialogDescription>{ejecuciones.length} ejecución(es)</DialogDescription>
          </DialogHeader>
          <div>
            {ejecuciones.length === 0 && (
              <div className="text-center py-8 text-neutral-400 text-[13px]">Sin ejecuciones aún.</div>
            )}
            {ejecuciones.length > 0 && (
              <table className="w-full text-[12px]">
                <thead className="bg-neutral-50 text-[10px] uppercase font-mono text-[#666]">
                  <tr>
                    <th className="px-3 py-1.5 text-left">Fecha</th>
                    <th className="px-3 py-1.5 text-left">Usuario</th>
                    <th className="px-3 py-1.5 text-right">Producido</th>
                    <th className="px-3 py-1.5 text-right">Costo total</th>
                    <th className="px-3 py-1.5 text-right">Costo/{historyReceta?.unidad_rendimiento || "kg"}</th>
                    <th className="px-3 py-1.5 text-left">Lote generado</th>
                  </tr>
                </thead>
                <tbody>
                  {ejecuciones.map((e) => (
                    <tr key={e.id} className="border-t">
                      <td className="px-3 py-1.5 font-mono text-[11px]">{fmtDate(e.created_at)}</td>
                      <td className="px-3 py-1.5">{e.username || "-"}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{fmtNum(e.cantidad_producida)} {e.unidad}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{fmtMoney(e.costo_total)}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{fmtMoney(e.costo_por_kg)}</td>
                      <td className="px-3 py-1.5 font-mono text-[11px]">{e.lote_generado_numero}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryReceta(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar borrado */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar receta "{confirmDelete?.nombre}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción sólo elimina la receta (plantilla). Las ejecuciones históricas y los lotes generados por ejecuciones anteriores quedan intactos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="receta-del-confirm">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
